import React, { useState } from 'react';
import VideoItem from './VideoItem';
import SectionQuizComponent from './SectionQuizComponent';

const SectionComponent = ({
  section,
  sectionIndex,
  isNested = false,
  parentSectionIndex = null,
  handleAddVideoToSection,
  handleRemoveSection,
  handleVideoInSectionChange,
  handlePdfInSectionChange,
  calculateSectionDuration,
  handleUploadVideoToServer,
  handleUploadPdfToServer,
  handleDeleteSection,
  handleDeleteVideo,
  handleDeletePdf,
  handleRemoveVideoFromSection,
  showToast,
  handleAddNestedSection,
  handleRemoveNestedSection,
  handleAddPdfToSection,
  handleRemovePdfFromSection,
  handleUploadStandalonePdfToServer,
  handleDeleteStandalonePdf,
  handleViewPdf,
  handleAddQuizToSection,
  handleSectionLockChange,
  handleStandalonePdfLockChange,
  courseData,
  refetchCourse
}) => {
  const [showNestedSectionForm, setShowNestedSectionForm] = useState(false);
  const [newNestedSectionName, setNewNestedSectionName] = useState('');

  const videoInputId = `video-upload-${isNested ? 'nested' : 'main'}-${sectionIndex}-${parentSectionIndex || ''}-${Date.now()}`;
  const pdfInputId = `pdf-upload-${isNested ? 'nested' : 'main'}-${sectionIndex}-${parentSectionIndex || ''}-${Date.now()}`;

  const getActualSectionIndex = () => {
    if (isNested && parentSectionIndex !== null) {
      return { 
        mainSectionIndex: parentSectionIndex, 
        nestedSectionIndex: sectionIndex, 
        isNested: true 
      };
    }
    return { 
      mainSectionIndex: sectionIndex, 
      isNested: false 
    };
  };

  const actualIndex = getActualSectionIndex();

  const safeSection = {
    ...section,
    videos: section.videos || [],
    pdfs: section.pdfs || [],
    quizzes: section.quizzes || [],
    nestedSections: section.nestedSections || [],
    name: section.name || 'Untitled Section',
    serverSectionId: section.serverSectionId || null,
    deleteStatus: section.deleteStatus || null,
    lockUpdateStatus: section.lockUpdateStatus || null,
    isLock: section.isLock !== undefined ? section.isLock : true
  };

  React.useEffect(() => {
    console.log('📋 SectionComponent - Section quizzes:', safeSection.quizzes);
  }, [safeSection.quizzes]);

  const handleAddNestedSectionLocal = () => {
    if (!newNestedSectionName.trim()) {
      showToast('Please enter a nested section name', 'error');
      return;
    }

    handleAddNestedSection(
      isNested ? parentSectionIndex : sectionIndex,
      newNestedSectionName.trim(),
      isNested
    );
    setNewNestedSectionName('');
    setShowNestedSectionForm(false);
  };

  const handleDeleteNestedSection = () => {
    if (window.confirm(`Are you sure you want to delete the nested section "${safeSection.name}"?`)) {
      handleRemoveNestedSection(parentSectionIndex, sectionIndex);
    }
  };

  const handleSectionLockToggle = (lockedValue) => {
    console.log(`🔄 Section lock change:`, {
      sectionId: safeSection.serverSectionId,
      sectionName: safeSection.name,
      isNested,
      parentSectionIndex,
      sectionIndex,
      lockedValue
    });

    if (isNested) {
      handleSectionLockChange(
        parentSectionIndex,
        lockedValue,
        true,
        sectionIndex
      );
    } else {
      handleSectionLockChange(
        sectionIndex,
        lockedValue,
        false
      );
    }
  };

  const handlePdfLockToggle = (pdfIndex, newLockStatus) => {
    const pdf = safeSection.pdfs[pdfIndex];
    
    if (!pdf) {
      console.error('❌ PDF not found:', { sectionIndex, pdfIndex, isNested, parentSectionIndex });
      showToast('PDF not found', 'error');
      return;
    }

    console.log('🔒 PDF Lock Toggle:', {
      pdfId: pdf.id,
      serverPdfId: pdf.serverPdfId,
      currentLockStatus: pdf.isLocked,
      newLockStatus,
      isNested,
      sectionIndex,
      parentSectionIndex
    });

    if (handleStandalonePdfLockChange) {
      handleStandalonePdfLockChange(
        isNested ? parentSectionIndex : sectionIndex,
        pdfIndex,
        isNested,
        isNested ? sectionIndex : null,
        newLockStatus
      );
    } else {
      console.error('❌ handleStandalonePdfLockChange is not provided');
      showToast('PDF lock function not available', 'error');
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('🎬 Video file selected:', {
        name: file.name,
        size: file.size,
        type: file.type,
        isNested,
        parentSectionIndex,
        sectionIndex
      });
      
      if (isNested) {
        handleAddVideoToSection(
          parentSectionIndex,
          file,
          true,
          sectionIndex
        );
      } else {
        handleAddVideoToSection(
          sectionIndex,
          file,
          false
        );
      }
    }
    e.target.value = '';
  };

  const handlePdfFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('📄 PDF file selected:', {
        name: file.name,
        size: file.size,
        type: file.type,
        isNested,
        parentSectionIndex,
        sectionIndex
      });
      
      if (isNested) {
        handleAddPdfToSection(
          parentSectionIndex,
          file,
          true,
          sectionIndex
        );
      } else {
        handleAddPdfToSection(
          sectionIndex,
          file,
          false
        );
      }
    }
    e.target.value = '';
  };

  return (
    <div key={safeSection.id || sectionIndex} className="border border-gray-200 rounded-xl mb-6 overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-900">
              {isNested ? (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  {safeSection.name}
                </span>
              ) : (
                safeSection.name
              )}
              {safeSection.isLock === true && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Locked
                </span>
              )}
              {safeSection.isLock === false && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  Unlocked
                </span>
              )}
              {safeSection.lockUpdateStatus === 'updating' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  <div className="w-3 h-3 mr-1 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </span>
              )}
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              {safeSection.videos.length} videos • {safeSection.pdfs.length} PDFs • {safeSection.quizzes.length} Quizzes • {calculateSectionDuration(safeSection.videos)}
              {safeSection.nestedSections.length > 0 && 
                ` • ${safeSection.nestedSections.length} nested sections`}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-3 mr-4 bg-white px-3 py-1.5 rounded-lg border border-gray-300">
              <span className="text-xs font-medium text-gray-700">Status:</span>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name={`section-lock-${safeSection.serverSectionId || sectionIndex}-${isNested ? 'nested' : 'main'}`}
                  checked={safeSection.isLock === true}
                  onChange={() => handleSectionLockToggle(true)}
                  disabled={safeSection.lockUpdateStatus === 'updating'}
                  className="text-red-500 focus:ring-red-500 disabled:opacity-50"
                />
                <span className="text-xs text-red-600 font-medium">Lock</span>
              </label>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name={`section-lock-${safeSection.serverSectionId || sectionIndex}-${isNested ? 'nested' : 'main'}`}
                  checked={safeSection.isLock === false}
                  onChange={() => handleSectionLockToggle(false)}
                  disabled={safeSection.lockUpdateStatus === 'updating'}
                  className="text-green-500 focus:ring-green-500 disabled:opacity-50"
                />
                <span className="text-xs text-green-600 font-medium">Unlock</span>
              </label>
            </div>

            {!isNested && (
              <button
                type="button"
                onClick={() => setShowNestedSectionForm(true)}
                className="px-3 py-1 bg-purple-500 text-white rounded-lg text-xs hover:bg-purple-600 transition-colors duration-300"
              >
                + Add Nested Section
              </button>
            )}
            
            {isNested ? (
              <button
                type="button"
                onClick={handleDeleteNestedSection}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-300"
                title="Delete nested section"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : (
              safeSection.serverSectionId && (
                <button
                  type="button"
                  onClick={() => handleDeleteSection(actualIndex.mainSectionIndex, actualIndex.isNested)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-300"
                  title="Delete section from server"
                  disabled={safeSection.deleteStatus === 'deleting'}
                >
                  {safeSection.deleteStatus === 'deleting' ? (
                    <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {showNestedSectionForm && !isNested && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={newNestedSectionName}
                onChange={(e) => setNewNestedSectionName(e.target.value)}
                placeholder="Enter nested section name"
                className="flex-1 px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddNestedSectionLocal}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors duration-300"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNestedSectionForm(false);
                  setNewNestedSectionName('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 mb-4 transition-all duration-300 hover:border-green-400 hover:bg-green-50">
          <div className="text-center">
            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <p className="text-sm text-gray-600 mb-3">Add video to this section</p>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoFileSelect}
              className="hidden"
              id={videoInputId}
            />
            <label
              htmlFor={videoInputId}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold cursor-pointer transition-all duration-300 hover:bg-green-600 inline-block text-sm"
            >
              Add Video
            </label>
          </div>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 mb-4 transition-all duration-300 hover:border-blue-400 hover:bg-blue-50">
          <div className="text-center">
            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-gray-600 mb-3">Add PDF to this section (without video)</p>
            <input
              type="file"
              accept=".pdf"
              onChange={handlePdfFileSelect}
              className="hidden"
              id={pdfInputId}
            />
            <label
              htmlFor={pdfInputId}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold cursor-pointer transition-all duration-300 hover:bg-blue-600 inline-block text-sm"
            >
              Add PDF
            </label>
          </div>
        </div>

        <div className="mb-6">
          <SectionQuizComponent
            sectionId={safeSection.serverSectionId}
            sectionName={safeSection.name}
            showToast={showToast}
            courseId={courseData?.id}
            refetchCourse={refetchCourse}
            existingQuizzes={safeSection.quizzes || []}
          />
        </div>

        {safeSection.pdfs.length > 0 && (
          <div className="mb-6">
            <h5 className="text-sm font-semibold text-gray-700 mb-3">PDFs in this section:</h5>
            <div className="space-y-3">
              {safeSection.pdfs.map((pdf, pdfIndex) => (
                <div key={pdf.id || `pdf-${pdfIndex}`} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{pdf.title || pdf.file?.name || 'Untitled PDF'}</p>
                        <p className="text-xs text-gray-500">
                          {pdf.file ? `${(pdf.file.size / (1024 * 1024)).toFixed(2)} MB` : 'Uploaded PDF'}
                          {pdf.serverPdfId && ` • ID: ${pdf.serverPdfId}`}
                          {pdf.isLocked !== undefined && ` • Status: ${pdf.isLocked ? '🔒 Locked' : '🔓 Unlocked'}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-3 mr-4 bg-white px-2 py-1 rounded-lg border border-gray-300">
                        <span className="text-xs font-medium text-gray-700">Status:</span>
                        <label className="flex items-center space-x-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`pdf-standalone-lock-${pdf.id || pdfIndex}-${isNested ? 'nested' : 'main'}`}
                            checked={pdf.isLocked === true}
                            onChange={() => handlePdfLockToggle(pdfIndex, true)}
                            disabled={pdf.lockUpdateStatus === 'updating' || !pdf.serverPdfId}
                            className="w-3 h-3 text-red-500 focus:ring-red-500 disabled:opacity-50"
                          />
                          <span className="text-xs text-red-600 font-medium">Lock</span>
                        </label>
                        <label className="flex items-center space-x-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`pdf-standalone-lock-${pdf.id || pdfIndex}-${isNested ? 'nested' : 'main'}`}
                            checked={pdf.isLocked === false}
                            onChange={() => handlePdfLockToggle(pdfIndex, false)}
                            disabled={pdf.lockUpdateStatus === 'updating' || !pdf.serverPdfId}
                            className="w-3 h-3 text-green-500 focus:ring-green-500 disabled:opacity-50"
                          />
                          <span className="text-xs text-green-600 font-medium">Unlock</span>
                        </label>
                        {pdf.lockUpdateStatus === 'updating' && (
                          <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                        )}
                      </div>
                      
                      {pdf.serverPdfUrl ? (
                        <>
                          <button
                            onClick={() => handleViewPdf(pdf.serverPdfUrl, pdf.title)}
                            className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors duration-300"
                          >
                            View PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (isNested) {
                                handleDeleteStandalonePdf(
                                  parentSectionIndex,
                                  pdfIndex,
                                  true,
                                  sectionIndex
                                );
                              } else {
                                handleDeleteStandalonePdf(
                                  sectionIndex,
                                  pdfIndex,
                                  false
                                );
                              }
                            }}
                            className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors duration-300"
                            disabled={pdf.deleteStatus === 'deleting'}
                          >
                            {pdf.deleteStatus === 'deleting' ? 'Deleting...' : 'Delete'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              const pdfNameToUse = (pdf.title || '').trim() || pdf.file?.name?.replace(/\.pdf$/i, '');
                              if (!pdfNameToUse) {
                                showToast('Please enter PDF name before upload.', 'error');
                                return;
                              }

                              if (isNested) {
                                handleUploadStandalonePdfToServer(
                                  parentSectionIndex,
                                  pdfIndex,
                                  true,
                                  sectionIndex
                                );
                              } else {
                                handleUploadStandalonePdfToServer(
                                  sectionIndex,
                                  pdfIndex,
                                  false
                                );
                              }
                            }}
                            className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors duration-300"
                            disabled={pdf.uploadStatus === 'uploading'}
                          >
                            {pdf.uploadStatus === 'uploading' ? 'Uploading...' : 'Upload PDF'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (isNested) {
                                handleRemovePdfFromSection(
                                  parentSectionIndex,
                                  pdfIndex,
                                  true,
                                  sectionIndex
                                );
                              } else {
                                handleRemovePdfFromSection(
                                  sectionIndex,
                                  pdfIndex,
                                  false
                                );
                              }
                            }}
                            className="px-3 py-1 text-red-500 hover:text-red-700 text-xs"
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      PDF Title *
                    </label>
                    <input
                      type="text"
                      value={pdf.title || ''}
                      onChange={(e) => {
                        if (isNested) {
                          handlePdfInSectionChange(
                            parentSectionIndex,
                            pdfIndex,
                            'title',
                            e.target.value,
                            true,
                            sectionIndex
                          );
                        } else {
                          handlePdfInSectionChange(
                            sectionIndex,
                            pdfIndex,
                            'title',
                            e.target.value,
                            false
                          );
                        }
                      }}
                      placeholder="Enter PDF title"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {!pdf.serverPdfUrl && (
                      <p className="text-xs text-gray-500 mt-1">
                        Final upload name: {(pdf.title || '').trim() ? `${pdf.title.trim()}.pdf` : (pdf.file?.name || 'document.pdf')}
                      </p>
                    )}
                  </div>
                  
                  {pdf.uploadStatus === 'uploading' && (
                    <div className="mt-2 flex items-center space-x-2 text-blue-600">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs">Uploading PDF...</span>
                    </div>
                  )}
                  {pdf.uploadStatus === 'success' && (
                    <div className="mt-2 flex items-center space-x-2 text-green-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs">PDF Uploaded Successfully</span>
                    </div>
                  )}
                  {pdf.uploadStatus === 'error' && (
                    <div className="mt-2 flex items-center space-x-2 text-red-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-xs">PDF Upload Failed</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {safeSection.nestedSections.length > 0 && !isNested && (
          <div className="mb-6">
            <h5 className="text-sm font-semibold text-gray-700 mb-3">Nested Sections:</h5>
            <div className="space-y-4 ml-6 pl-4 border-l-2 border-gray-200">
              {safeSection.nestedSections.map((nestedSection, nestedIndex) => (
                <SectionComponent
                  key={nestedSection.id || `nested-${nestedIndex}`}
                  section={nestedSection}
                  sectionIndex={nestedIndex}
                  isNested={true}
                  parentSectionIndex={sectionIndex}
                  handleAddVideoToSection={handleAddVideoToSection}
                  handleRemoveSection={handleRemoveNestedSection}
                  handleVideoInSectionChange={handleVideoInSectionChange}
                  handlePdfInSectionChange={handlePdfInSectionChange}
                  calculateSectionDuration={calculateSectionDuration}
                  handleUploadVideoToServer={handleUploadVideoToServer}
                  handleUploadPdfToServer={handleUploadPdfToServer}
                  handleDeleteSection={handleDeleteSection}
                  handleDeleteVideo={handleDeleteVideo}
                  handleDeletePdf={handleDeletePdf}
                  handleRemoveVideoFromSection={handleRemoveVideoFromSection}
                  showToast={showToast}
                  handleAddNestedSection={handleAddNestedSection}
                  handleRemoveNestedSection={handleRemoveNestedSection}
                  handleAddPdfToSection={handleAddPdfToSection}
                  handleRemovePdfFromSection={handleRemovePdfFromSection}
                  handleUploadStandalonePdfToServer={handleUploadStandalonePdfToServer}
                  handleDeleteStandalonePdf={handleDeleteStandalonePdf}
                  handleViewPdf={handleViewPdf}
                  handleAddQuizToSection={handleAddQuizToSection}
                  handleSectionLockChange={handleSectionLockChange}
                  handleStandalonePdfLockChange={handleStandalonePdfLockChange}
                  courseData={courseData}
                  refetchCourse={refetchCourse}
                />
              ))}
            </div>
          </div>
        )}

        {safeSection.videos.length > 0 && (
          <div className="space-y-4">
            {safeSection.videos.map((video, videoIndex) => (
              <VideoItem
                key={video.id || `video-${videoIndex}`}
                sectionIndex={isNested ? parentSectionIndex : sectionIndex}
                videoIndex={videoIndex}
                video={video}
                handleVideoInSectionChange={handleVideoInSectionChange}
                handleUploadVideoToServer={handleUploadVideoToServer}
                handleUploadPdfToServer={handleUploadPdfToServer}
                handleDeleteVideo={handleDeleteVideo}
                handleDeletePdf={handleDeletePdf}
                handleRemoveVideoFromSection={handleRemoveVideoFromSection}
                showToast={showToast}
                isNested={isNested}
                nestedSectionIndex={isNested ? sectionIndex : null}
                handleViewPdf={handleViewPdf}
                courseId={courseData?.id}
                refetchCourse={refetchCourse}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionComponent;